import { useState, useEffect, useCallback, useRef } from 'react';
import { getFavorites, addFavorite as addFavLocal, removeFavorite as removeFavLocal } from '@/lib/storage';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import type { Favorite } from '@/types';

interface UseFavoritesOptions {
  userId?: string | null;
  isAuthenticated?: boolean;
}

export function useFavorites(options: UseFavoritesOptions = {}) {
  const { userId, isAuthenticated = false } = options;
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  // Load favorites from localStorage or Supabase
  const loadFavorites = useCallback(async () => {
    try {
      // Load from localStorage first
      const localFavs = await getFavorites();
      setFavorites(localFavs);
      setLoading(false);

      // If authenticated and not yet loaded from cloud, load from Supabase
      if (isAuthenticated && userId && supabase && isSupabaseConfigured() && !loadedRef.current) {
        loadedRef.current = true;
        try {
          const { data, error } = await supabase
            .from('user_favorites')
            .select('song_id, created_at')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

          if (!error && data && data.length > 0) {
            const cloudFavs: Favorite[] = data.map(item => ({
              songId: item.song_id,
              addedAt: new Date(item.created_at).getTime()
            }));
            setFavorites(cloudFavs);
            localStorage.setItem('jem-favorites', JSON.stringify(cloudFavs));
            console.log('Favorites loaded from cloud:', cloudFavs.length);
          } else if (error) {
            console.warn('Cloud favorites unavailable:', error.message);
          }
        } catch (cloudError) {
          console.warn('Cloud sync error, using local favorites');
        }
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
      setLoading(false);
    }
  }, [isAuthenticated, userId]);

  // Reset ref when user changes
  useEffect(() => {
    loadedRef.current = false;
  }, [userId]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Add favorite
  const addFavorite = useCallback(async (songId: number) => {
    try {
      await addFavLocal(songId);

      // Update state immediately
      setFavorites(prev => {
        if (prev.some(f => f.songId === songId)) return prev;
        return [...prev, { songId, addedAt: Date.now() }];
      });

      // Sync to cloud if authenticated
      if (isAuthenticated && userId && supabase && isSupabaseConfigured()) {
        try {
          const { error } = await supabase
            .from('user_favorites')
            .insert({ user_id: userId, song_id: songId });

          if (error && error.code !== '23505') {
            console.warn('Cloud sync add failed:', error.message);
          } else {
            console.log('Favorite synced to cloud:', songId);
          }
        } catch (cloudError) {
          console.warn('Cloud add error:', cloudError);
        }
      }

      return true;
    } catch (error) {
      console.error('Error adding favorite:', error);
      return false;
    }
  }, [isAuthenticated, userId]);

  // Remove favorite
  const removeFavorite = useCallback(async (songId: number) => {
    try {
      await removeFavLocal(songId);

      // Update state immediately
      setFavorites(prev => prev.filter(f => f.songId !== songId));

      // Sync to cloud if authenticated
      if (isAuthenticated && userId && supabase && isSupabaseConfigured()) {
        try {
          const { error } = await supabase
            .from('user_favorites')
            .delete()
            .eq('user_id', userId)
            .eq('song_id', songId);

          if (error) {
            console.warn('Cloud sync delete failed:', error.message);
          } else {
            console.log('Favorite removed from cloud:', songId);
          }
        } catch (cloudError) {
          console.warn('Cloud delete error:', cloudError);
        }
      }

      return true;
    } catch (error) {
      console.error('Error removing favorite:', error);
      return false;
    }
  }, [isAuthenticated, userId]);

  // Toggle favorite
  const toggleFavorite = useCallback(async (songId: number) => {
    const isFav = favorites.some(fav => fav.songId === songId);
    if (isFav) {
      return await removeFavorite(songId);
    } else {
      return await addFavorite(songId);
    }
  }, [favorites, addFavorite, removeFavorite]);

  // Check if is favorite (synchronous)
  const isFavorite = useCallback((songId: number) => {
    return favorites.some(fav => fav.songId === songId);
  }, [favorites]);

  return {
    favorites,
    loading,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    reload: loadFavorites,
  };
}
